import * as React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ShieldCheck, Archive, FlaskConical } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { env } from '@/lib/env';

export const Route = createFileRoute('/_app/operator/forbidden')({
  staticData: { crumb: 'Forbidden claims' },
  beforeLoad: ({ context }) => {
    const ctx = context as { session: { role: string } };
    if (ctx.session.role !== 'operator') throw redirect({ to: '/' });
  },
  component: OperatorForbidden,
});

type ClaimState = 'draft' | 'approved' | 'archived';
type Authority = 'KIWL' | 'KIDL' | 'UoKiK' | 'lawyer' | 'other';

interface ClaimRow {
  id: string;
  vertical: string;
  pattern: string;
  rationale: string;
  authority: Authority;
  state: ClaimState;
  reviewed_by: string | null;
  reviewed_at: number | null;
  updated_at: number;
}

const STATE_LABEL: Record<ClaimState, string> = {
  draft: 'Szkic',
  approved: 'Aktywna',
  archived: 'Zarchiwizowana',
};

const VERTICALS = ['vet', 'medical'] as const;

function OperatorForbidden() {
  const [vertical, setVertical] = React.useState<string>('vet');
  const [editing, setEditing] = React.useState<ClaimRow | 'new' | null>(null);

  const list = useQuery({
    queryKey: ['operator', 'forbidden', vertical],
    queryFn: async (): Promise<ClaimRow[]> => {
      const url = new URL(`${env.apiBaseUrl}/api/operator/forbidden`, window.location.origin);
      url.searchParams.set('vertical', vertical);
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error('failed_to_load');
      const j = (await res.json()) as { rows: ClaimRow[] };
      return j.rows;
    },
  });

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
            Forbidden claims
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            Per-wertykalna lista wyrażeń, które nie mogą pojawić się w copy
            (regex). Reklama medyczna i weterynaryjna w PL podlega ograniczeniom
            KIWL / KIDL / UoKiK — każda reguła ma rationale i autorytet źródła.
          </p>
        </div>
        <Button type="button" onClick={() => setEditing('new')}>
          <Plus className="size-4" aria-hidden="true" /> Nowa reguła
        </Button>
      </header>

      <Tabs value={vertical} onValueChange={setVertical}>
        <TabsList>
          {VERTICALS.map((v) => (
            <TabsTrigger key={v} value={v} className="uppercase tracking-wide text-xs">
              {v}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            {list.data ? `${list.data.length} reguł` : 'Ładowanie…'}
          </CardTitle>
          <CardDescription>
            Live renderer czyta plik YAML w repo; zmiany tu są draftami — synchronizuj
            YAML ręcznie po zatwierdzeniu (auto-PR jest follow-upem).
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {list.data && list.data.length > 0 ? (
            <ul role="list" className="divide-y divide-border">
              {list.data.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-surface/60"
                >
                  <div className="min-w-0 flex-1">
                    <p className="flex items-baseline gap-2">
                      <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-xs text-text ring-1 ring-border">
                        {r.pattern}
                      </code>
                      <StateBadge state={r.state} />
                      <AuthorityBadge auth={r.authority} />
                    </p>
                    <p className="mt-1 text-xs text-text-muted">{r.rationale}</p>
                  </div>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(r)}>
                    Otwórz
                  </Button>
                </li>
              ))}
            </ul>
          ) : list.isPending ? null : (
            <p className="p-4 text-sm text-text-muted">Brak reguł dla tego wertykała.</p>
          )}
        </CardContent>
      </Card>

      {editing ? (
        <ClaimEditor
          initial={editing === 'new' ? null : editing}
          defaultVertical={vertical}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function StateBadge({ state }: { state: ClaimState }) {
  const variant: Record<ClaimState, 'default' | 'success' | 'outline'> = {
    draft: 'default',
    approved: 'success',
    archived: 'outline',
  };
  return <Badge variant={variant[state]} className="text-[10px]">{STATE_LABEL[state]}</Badge>;
}

function AuthorityBadge({ auth }: { auth: Authority }) {
  return <Badge variant="soft" className="text-[10px] font-mono">{auth}</Badge>;
}

function ClaimEditor({
  initial,
  defaultVertical,
  onClose,
}: {
  initial: ClaimRow | null;
  defaultVertical: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [vertical, setVertical] = React.useState(initial?.vertical ?? defaultVertical);
  const [pattern, setPattern] = React.useState(initial?.pattern ?? '');
  const [rationale, setRationale] = React.useState(initial?.rationale ?? '');
  const [authority, setAuthority] = React.useState<Authority>(initial?.authority ?? 'KIWL');
  const [reviewedBy, setReviewedBy] = React.useState('');
  const [testInput, setTestInput] = React.useState('');
  const [err, setErr] = React.useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${env.apiBaseUrl}/api/operator/forbidden`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          id: initial?.id,
          vertical,
          pattern,
          rationale,
          authority,
        }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
        throw new Error(j.detail ?? j.error ?? 'save_failed');
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operator', 'forbidden'] }),
    onError: (e) => setErr((e as Error).message),
  });

  const approve = useMutation({
    mutationFn: async () => {
      if (!initial) throw new Error('Zapisz najpierw szkic.');
      if (!reviewedBy) throw new Error('Wpisz dane recenzenta.');
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/forbidden/${initial.id}/approve`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ reviewed_by: reviewedBy }),
        }
      );
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'approve_failed');
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operator', 'forbidden'] }),
    onError: (e) => setErr((e as Error).message),
  });

  const archive = useMutation({
    mutationFn: async () => {
      if (!initial) return;
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/forbidden/${initial.id}/archive`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) throw new Error('archive_failed');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operator', 'forbidden'] });
      onClose();
    },
  });

  // Live regex test.
  const testResult = React.useMemo(() => {
    if (!pattern || !testInput) return null;
    try {
      const re = new RegExp(pattern, 'iu');
      const hits = testInput
        .split(/\n/)
        .map((line, idx) => ({ line, idx, match: re.test(line) }))
        .filter((r) => r.match);
      return { ok: true as const, hits };
    } catch (e) {
      return { ok: false as const, error: (e as Error).message };
    }
  }, [pattern, testInput]);

  const state: ClaimState = initial?.state ?? 'draft';
  const canEdit = state !== 'archived';

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-baseline gap-2">
            {initial ? 'Edytuj regułę' : 'Nowa reguła'}
            {initial ? <StateBadge state={state} /> : null}
          </DialogTitle>
          <DialogDescription>
            Wprowadź regex case-insensitive Unicode i krótkie rationale. Po zatwierdzeniu
            reguła trafia na renderer.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Wertykał" htmlFor="vert">
            <Select
              id="vert"
              value={vertical}
              onChange={(e) => setVertical(e.target.value)}
              disabled={!!initial}
            >
              {VERTICALS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Autorytet" htmlFor="auth">
            <Select
              id="auth"
              value={authority}
              onChange={(e) => setAuthority(e.target.value as Authority)}
              disabled={!canEdit}
            >
              {(['KIWL', 'KIDL', 'UoKiK', 'lawyer', 'other'] as Authority[]).map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field
          label="Pattern (regex)"
          htmlFor="pat"
          hint="Case-insensitive Unicode regex. Word-boundary stosowany na poziomie pipeline'u."
        >
          <Input
            id="pat"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            disabled={!canEdit}
            className="font-mono text-xs"
          />
        </Field>

        <Field
          label="Rationale"
          htmlFor="rat"
          hint="Krótkie wyjaśnienie — dlaczego ten zwrot jest zakazany?"
        >
          <Textarea
            id="rat"
            rows={2}
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            disabled={!canEdit}
          />
        </Field>

        <Field
          label="Test (paste sample copy)"
          htmlFor="test"
          hint="Każda linia jest sprawdzana niezależnie. Trafienia podświetlone na czerwono."
        >
          <Textarea
            id="test"
            rows={4}
            value={testInput}
            onChange={(e) => setTestInput(e.target.value)}
            placeholder={'Tu wklej fragment copy do sprawdzenia.\nNp. „Gwarantujemy 100% skuteczności"'}
            className="font-mono text-xs"
          />
        </Field>

        {testResult ? (
          testResult.ok ? (
            testResult.hits.length > 0 ? (
              <div className="space-y-1 rounded-md bg-error/10 p-3 text-xs text-error ring-1 ring-error/20">
                <p className="font-semibold">
                  <FlaskConical className="mr-1 inline size-3" aria-hidden="true" />
                  {testResult.hits.length} trafień
                </p>
                {testResult.hits.map((h) => (
                  <p key={h.idx} className="font-mono">
                    L{h.idx + 1}: {h.line}
                  </p>
                ))}
              </div>
            ) : (
              <p className="rounded-md bg-success/10 p-2 text-xs text-success ring-1 ring-success/20">
                Brak trafień w tym sample.
              </p>
            )
          ) : (
            <p className="rounded-md bg-warning/10 p-2 text-xs text-warning ring-1 ring-warning/20">
              Regex nie kompiluje się: {testResult.error}
            </p>
          )
        ) : null}

        {err ? (
          <p className="rounded-md bg-error/10 px-3 py-2 text-sm text-error ring-1 ring-error/20">
            {err}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <Button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending || !canEdit || !pattern || !rationale}
          >
            Zapisz
          </Button>

          {initial && state === 'draft' ? (
            <>
              <Input
                placeholder="KIWL/PWZ recenzenta"
                value={reviewedBy}
                onChange={(e) => setReviewedBy(e.target.value)}
                className="h-9 w-56"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => approve.mutate()}
                disabled={approve.isPending || !reviewedBy}
              >
                <ShieldCheck className="size-4" aria-hidden="true" /> Zatwierdź
              </Button>
            </>
          ) : null}

          {initial && state !== 'archived' ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="ghost" disabled={archive.isPending} className="ml-auto">
                  <Archive className="size-4" aria-hidden="true" /> Archiwizuj
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Zarchiwizować tę regułę?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Reguła przestanie blokować copy w renderze. Można odtworzyć przez
                    edycję wpisu.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Anuluj</AlertDialogCancel>
                  <AlertDialogAction onClick={() => archive.mutate()}>Zarchiwizuj</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
